import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Box, Button, TextField, Typography, Paper, Alert, CircularProgress,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import AuthShell from "../components/AuthShell";
import { alpha } from "@mui/material/styles";

const NAVY    = "#0F2156";
const CRIMSON = "#B91C2C";
const GOLD    = "#E8A020";

/** Map known English backend error strings → translated UI messages */
function translateAuthError(message: string, t: (k: string) => string): string {
  const lower = message.toLowerCase();
  if (
    lower.includes("invalid") &&
    (lower.includes("password") || lower.includes("email") || lower.includes("username") || lower.includes("credentials"))
  ) {
    return t("auth.invalidCredentials");
  }
  if (lower.includes("verify") || lower.includes("verification") || lower.includes("not active") && lower.includes("email")) {
    return t("auth.verifyEmailFirst");
  }
  if (lower.includes("not active")) {
    return t("auth.accountNotActive");
  }
  return message;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useLanguage();
  const routeState = location.state as { successMessage?: string } | null;
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailOrUsername.trim()) { setError(t("auth.emailOrUsernameRequired")); return; }
    if (!password) { setError(t("auth.passwordRequired")); return; }

    setLoading(true);
    setError(null);
    try {
      await login({ email_or_username: emailOrUsername, password });
      navigate("/app/feed");
    } catch (err) {
      setError(err instanceof Error ? translateAuthError(err.message, t) : t("auth.invalidCredentials"));
    } finally {
      setLoading(false);
    }
  };

  const headerRight = (
    <Button
      variant="outlined"
      size="small"
      onClick={() => navigate("/signup")}
      sx={{
        color: "white",
        borderColor: alpha("#fff", 0.4),
        fontWeight: 600,
        textTransform: "none",
        fontSize: "0.82rem",
        "&:hover": { borderColor: "white", bgcolor: alpha("#fff", 0.08) },
      }}
    >
      {t("auth.signup")}
    </Button>
  );

  return (
    <AuthShell rightContent={headerRight}>
      <Box
        sx={{
          minHeight: "calc(100vh - 56px - 60px)",
          background: `linear-gradient(145deg, ${NAVY} 0%, #1a3a7a 45%, ${alpha(CRIMSON, 0.85)} 100%)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: alpha(GOLD, 0.1),
            filter: "blur(80px)",
            pointerEvents: "none",
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 400,
            p: { xs: 3, sm: 4 },
            borderRadius: "20px",
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
            position: "relative", zIndex: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: 3,
              borderRadius: "20px 20px 0 0",
              background: `linear-gradient(90deg, ${CRIMSON} 0%, ${NAVY} 100%)`,
            },
          }}
        >
          <Typography
            variant="h5"
            fontWeight={800}
            mb={2.5}
            textAlign="center"
            sx={{ color: "#1A1A2E", letterSpacing: "-0.02em" }}
          >
            {t("auth.welcomeBack")}
          </Typography>

          {routeState?.successMessage && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {routeState.successMessage}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label={t("auth.emailOrUsername")}
              fullWidth
              margin="normal"
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              placeholder={t("auth.emailOrUsernamePlaceholder")}
              required
              autoComplete="username"
            />
            <TextField
              label={t("auth.password")}
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("auth.passwordPlaceholder")}
              required
              autoComplete="current-password"
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 2.5, mb: 1.5, py: 1.4, borderRadius: 12, fontWeight: 700, fontSize: "0.95rem" }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : t("auth.login")}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Typography variant="body2" color="text.secondary" component="span">
              {t("auth.noAccount")}{" "}
            </Typography>
            <Typography
              variant="body2"
              component={Link}
              to="/signup"
              sx={{ textDecoration: "none", fontWeight: 700, color: CRIMSON }}
            >
              {t("auth.signup")}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Typography
              variant="body2"
              component={Link}
              to="/forgot-password"
              sx={{ textDecoration: "none", color: "#9CA3AF", fontSize: "0.82rem", "&:hover": { color: CRIMSON } }}
            >
              {t("auth.forgotPassword")}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center", mt: 0.75 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.82rem" }} component="span">
              {t("auth.verifyEmailPrompt")}{" "}
            </Typography>
            <Typography
              variant="body2"
              component={Link}
              to="/verify-email"
              sx={{ textDecoration: "none", fontWeight: 600, color: NAVY, fontSize: "0.82rem", "&:hover": { color: CRIMSON } }}
            >
              {t("auth.verifyYourEmail")}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </AuthShell>
  );
}
